import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import type { ViewEntityType } from "@colab/shared";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useUpdateView } from "@/features/admin/views/use-update-view";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  viewId: string;
  entityType: ViewEntityType;
  currentName: string;
};

export function RenameViewDialog({
  open,
  onOpenChange,
  viewId,
  entityType,
  currentName,
}: Props) {
  const [name, setName] = useState(currentName);
  const updateView = useUpdateView();

  useEffect(() => {
    if (open) setName(currentName);
  }, [open, currentName]);

  function submit() {
    const trimmed = name.trim();
    if (!trimmed) {
      toast.error("Name is required");
      return;
    }
    if (trimmed === currentName) {
      onOpenChange(false);
      return;
    }
    updateView.mutate(
      { id: viewId, entityType, patch: { name: trimmed } },
      {
        onSuccess: () => {
          toast.success("Renamed");
          onOpenChange(false);
        },
      },
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight">
            Rename view
          </DialogTitle>
          <DialogDescription>
            Choose a new display name. Visible in the tab strip.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-1.5">
          <Label htmlFor="rename-view" className="text-xs">
            Name
          </Label>
          <Input
            id="rename-view"
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoFocus
            maxLength={200}
          />
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={updateView.isPending}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={submit}
            disabled={updateView.isPending || !name.trim()}
          >
            {updateView.isPending && (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            )}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
